// note3:// URI handling — turns an OS-delivered deep link into an in-app route.
//
// A note3:// URI mirrors the app's hash route 1:1, so these are equivalent:
//   note3://block/7ae15a4?preview=baguqeera4d...   ==   <web>/#/block/7ae15a4?preview=baguqeera4d...
//
// Two delivery paths are wired here:
//  - Tauri (desktop + Android): the deep-link plugin hands us the raw url via
//    getCurrent() (cold start) and onOpenUrl() (while running).
//  - PWA: the web app manifest registers a `web+note3` protocol handler that
//    launches the app at `/?protocol_handler=<encoded-uri>`; we consume it on
//    startup. (Browsers only allow custom schemes with a `web+` prefix, so the
//    PWA scheme is `web+note3://…` even though it maps to the same routes.)

import { Logger } from 'besonders-logger'

const { WARN, LOG, DEBUG, ERROR } = Logger.setup(Logger.INFO, { prefix: '[deep-link]' }) // eslint-disable-line unused-imports/no-unused-vars

// Matches the scheme prefix of both the native (note3://) and PWA (web+note3://)
// forms, tolerating 0–3 slashes (note3:foo / note3:/foo / note3://foo / note3:///foo).
const SCHEME_RE = /^(?:web\+)?note3:\/*/i

/**
 * Map an incoming deep-link URI (or a full web share URL) to the app's hash
 * route and navigate there. Returns true if it navigated.
 *
 * `replace` controls whether this consumes a history entry. On a **cold start**
 * we must replace: the only entry in history at that point is the blank
 * `#`/start-page entry the webview just loaded, and pushing on top of it means
 * the first back press walks into the start page (which then redirects to the
 * timeline) instead of leaving the app. On Android that made a deep link from
 * another app cost *two* back presses, the second one landing on the launcher
 * rather than the caller. For a **warm** link (onOpenUrl while the app is
 * already running) we push, so back returns to whatever the user was looking at.
 */
export function handleDeepLinkUrl(uri: string, { replace = false }: { replace?: boolean } = {}): boolean {
	if (!uri) return false

	let route: string | null = null
	if (SCHEME_RE.test(uri)) {
		// note3://block/ID?preview=CID  →  /block/ID?preview=CID
		route = '/' + uri.replace(SCHEME_RE, '')
	} else {
		// A full web link (e.g. https://note3.app/#/block/…) — reuse its hash route.
		try {
			const url = new URL(uri)
			if (url.hash?.startsWith('#/')) route = url.hash.slice(1)
		} catch { /* not a parseable URL — ignore */ }
	}

	if (!route || route === '/') {
		WARN(`ignoring un-routable deep link:`, uri)
		return false
	}

	LOG(`deep link →`, route, { replace })
	if (replace) {
		// replaceState (not `location.hash = …`) so no extra history entry is
		// created. Safe here because every `replace: true` caller runs *before*
		// the router mounts, so the router reads the final hash on first render
		// and no hashchange listener is missed.
		history.replaceState(null, '', `#${route}`)
	} else {
		window.location.hash = `#${route}`
	}
	return true
}

/**
 * Wire up OS-level deep-link delivery. Call once at startup, before the router
 * mounts, so a cold-start link lands on the right route instead of the default
 * start page.
 */
export async function setupDeepLinks(): Promise<void> {
	// PWA protocol-handler launch: `/?protocol_handler=<encoded note3 uri>`
	try {
		const params = new URLSearchParams(window.location.search)
		const handlerUri = params.get('protocol_handler')
		if (handlerUri) {
			DEBUG(`PWA protocol_handler launch:`, handlerUri)
			handleDeepLinkUrl(decodeURIComponent(handlerUri), { replace: true })
			// Drop the param so a reload doesn't replay the old link.
			params.delete('protocol_handler')
			const qs = params.toString()
			history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash)
		}
	} catch (err) {
		WARN(`failed to consume PWA protocol_handler param`, err)
	}

	// Tauri (desktop + Android): native delivery via the deep-link plugin.
	if ((window as any).isTauri) {
		try {
			// dynamic import so the plugin guest-js only loads under Tauri
			const { getCurrent, onOpenUrl } = await import('@tauri-apps/plugin-deep-link')
			const initial = await getCurrent().catch(() => null)
			if (initial?.length) {
				DEBUG(`tauri cold-start deep link:`, initial)
				// replace: the blank start-page entry we'd otherwise leave behind
				// costs an extra back press before the app can be left (see above).
				handleDeepLinkUrl(initial[0], { replace: true })
			}
			await onOpenUrl((urls) => {
				DEBUG(`tauri onOpenUrl:`, urls)
				if (urls?.length) handleDeepLinkUrl(urls[0])
			})
			LOG(`tauri deep-link listener registered`)
		} catch (err) {
			WARN(`tauri deep-link setup failed (plugin unavailable?)`, err)
		}
	}
}
